import React, { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { EntryRegistrationFormValues } from "../../../../../views/LogisticPark/RegistroIngreso/components/types";
import { RegistroIngresoSessionContext } from "../../../../../views/LogisticPark/RegistroIngreso/session/RegistroIngresoSessionContext";
import AuthorizationsSection from "./AuthorizationsSection";
import "./AccessTypeFormContent.css";

type AccessTypeFormContentProps = {
  fieldPrefix: string;
  authorizations?: any[];
  disabled?: boolean;
};

const AccessTypeFormContent: React.FC<AccessTypeFormContentProps> = ({
  fieldPrefix,
  authorizations = [],
  disabled = false,
}) => {
  const {
    watch,
    setValue,
    register,
    unregister,
    formState: { isSubmitted },
  } = useFormContext<EntryRegistrationFormValues>();

  const session = React.useContext(RegistroIngresoSessionContext);
  const activeTab = session?.tabs?.find((tab) => tab.id === session.activeTabId);
  const isSearching = activeTab?.isSearching ?? false;
  const isExit = activeTab?.mode === "exit";
  const entranceEvent = watch("entranceEvent");

  const currentConfirmations = watch("employeeConfirmations") || [];
  const currentPrevalidations = watch("prevalidation") || [];
  const currentInvites = watch("invites") || [];
  const currentAuthorizations = watch("authorizations") || [];

  const isValid =
    currentConfirmations.length > 0 ||
    currentPrevalidations.length > 0 ||
    currentInvites.length > 0 ||
    currentAuthorizations.length > 0 ||
    // En el egreso la autorización es la del ingreso, que ya pasó por esta misma validación: acá
    // sólo se muestra prefilleada. Exigirla de nuevo deja encerrado a quien entró sin una
    // reutilizable — como el acompañante del transportista, cuya confirmación en guardia se
    // prefillea sin anfitrión (ver buildDefaultEntryRegistrationValues / isTransportCompanionEvent).
    (isExit && !!entranceEvent);
  const hasError = isSubmitted && !isValid;

  // El formulario padre resetea sus valores cuando llega el resultado de la búsqueda (ver
  // `reset(values, ...)` en EntryRegistrationForm), lo que borra el valor guardado de este campo
  // sintético sin pasar por `setValue`. Si en ese momento `isValid` no cambia de valor (p.ej. ya
  // era `true` antes y después del reset, como con una confirmación prefilleada), el efecto de
  // abajo no se vuelve a disparar y el campo queda vacío aunque la tarjeta siga seleccionada. Un
  // ref evita depender de ese timing: `validate` siempre lee el `isValid` más reciente al momento
  // del submit, sin importar qué haya quedado guardado en el campo.
  const isValidRef = useRef(isValid);
  useEffect(() => {
    isValidRef.current = isValid;
  }, [isValid]);

  // El formulario no usa `shouldUnregister`, así que sin el cleanup este campo sobrevive al
  // desmontaje: al cambiar a Transportista (que valida con `transportAuthorizationSelected`) seguía
  // exigiendo una confirmación/invitación de Visita, Eventual o Contratista y bloqueaba el ingreso.
  useEffect(() => {
    register("authorizationSelected" as any, {
      validate: () => isValidRef.current || "El tipo de autorización es obligatorio",
    });
    return () => {
      unregister("authorizationSelected" as any);
    };
  }, [register, unregister]);

  useEffect(() => {
    setValue("authorizationSelected" as any, isValid ? "true" : "", {
      shouldValidate: isSubmitted,
    });
  }, [isValid, setValue, isSubmitted]);

  return (
    <div className="accessTypeFormContentContainer" data-field-name="authorizationSelected">
      <AuthorizationsSection
        id={`${fieldPrefix}-access-type-authorizations`}
        disabled={disabled}
        hasError={hasError}
        searchFieldName={`${fieldPrefix}Search`}
        confirmationsFieldName="employeeConfirmations"
        confirmationsDataFieldName="employeeConfirmationsData"
        prevalidationsFieldName="prevalidation"
        invitesFieldName="invites"
        authorizationsFieldName="authorizations"
        companyFieldName="authorizationCompany"
        authorizationsList={authorizations}
        isLoadingAuths={isSearching}
      />
    </div>
  );
};

export default AccessTypeFormContent;
